/**
 * PranaFlow Academy LMS
 * Core API Client (Fetch with session credentials)
 */

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/my-lms/backend/api';

/**
 * Handle HTTP responses and parse JSON
 */
async function handleResponse(response) {
  const rawText = await response.text();
  let payload;
  try {
    payload = JSON.parse(rawText);
  } catch (e) {
    throw new Error(`Invalid API server response format.\nURL: ${response.url}\nStatus: ${response.status} ${response.statusText}\nRaw Response:\n${rawText}`);
  }

  if (!response.ok || payload.success === false) {
    const err = new Error(payload.message || 'API connection failed');
    err.errors = payload.errors || {};
    err.statusCode = response.status;
    err.code = payload.code || null;
    err.status = payload.status || null;
    err.student_name = payload.student_name || null;
    err.student_email = payload.student_email || null;
    err.heading = payload.heading || null;
    err.raw_status = payload.raw_status || null;
    err.rawResponse = rawText;
    throw err;
  }
  return payload.user !== undefined ? payload.user : (payload.data !== undefined ? payload.data : payload);
}

/**
 * Helper to construct request headers including Authorization Bearer token
 */
function getHeaders(customHeaders = {}) {
  const headers = { ...customHeaders };
  const token = localStorage.getItem('token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export async function apiGet(endpoint, options = {}) {
  const customHeaders = options.headers || {};
  const headers = {
    "Accept": "application/json",
    ...customHeaders
  };
  const token = localStorage.getItem('token');
  if (token && !options.skipAuth) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: "GET",
    headers,
    credentials: options.credentials || "include"
  });
  return handleResponse(response);
}

/**
 * Perform a Public HTTP GET Request (No Authorization header, no credentials required)
 */
export async function apiPublicGet(endpoint) {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: "GET",
    headers: {
      "Accept": "application/json"
    },
    credentials: "omit"
  });
  return handleResponse(response);
}

/**
 * Perform an HTTP POST Request
 */
export async function apiPost(endpoint, data = {}) {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: "POST",
    headers: getHeaders({
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest"
    }),
    credentials: "include",
    body: JSON.stringify(data)
  });
  return handleResponse(response);
}

/**
 * Perform an HTTP PUT Request
 */
export async function apiPut(endpoint, data = {}) {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: "PUT",
    headers: getHeaders({
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest"
    }),
    credentials: "include",
    body: JSON.stringify(data)
  });
  return handleResponse(response);
}

/**
 * Perform an HTTP DELETE Request
 */
export async function apiDelete(endpoint, data = {}) {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: "DELETE",
    headers: getHeaders({
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest"
    }),
    credentials: "include",
    body: JSON.stringify(data)
  });
  return handleResponse(response);
}
